import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import { graphqlRequest } from '~/adapters/graphqlRequest';
import {
  CreateOrUpdateSurveyReminderDocument,
  CreateOrUpdateSurveyReminderMutationResult,
  DeleteSurveyReminderDocument,
  DeleteSurveyReminderMutationResult,
  FindSurveyReminderByReminderIdDocument,
  FindSurveyReminderDetailsBySurveyIdDocument,
  FindSurveyReminderDetailsBySurveyIdQueryResult,
  FindSurveyRemindersBySurveyIdDocument,
  FindSurveyRemindersBySurveyIdQueryResult,
} from '~/gql/graphql';
import * as transformers from '~/behaviors/transformers';
import * as schemas from '~/behaviors/schemas';
import * as languages from '~/behaviors/languages';
import { reminderSettings } from '~/config/settings/reminder.settings';
import { statuses as notificationStatuses } from '~/config/settings/notification.settings';
import { datetime } from '~/tools/datetime';
import { SurveyReminder } from '~/interfaces/survey.interface';

const findSurveyRemindersBySurveyId = createAsyncThunk<
  { entities: { surveyReminders: { [id: number]: SurveyReminder } } },
  { surveyId: number }
>('surveyReminders/findSurveyRemindersBySurveyId', async (surveyId, { rejectWithValue }) => {
  try {
    const res = await graphqlRequest<FindSurveyRemindersBySurveyIdQueryResult>(FindSurveyRemindersBySurveyIdDocument, {
      surveyId,
    });
    const normalizedData: { entities: { surveyReminders: { [id: number]: any } } } = normalize(
      res.data?.findSurveyRemindersBySurveyId,
      [schemas.surveyReminder],
    );

    return normalizedData;
  } catch (e) {
    return rejectWithValue(e);
  }
});

const upsertReminder = createAsyncThunk<
  | {
      entities: { surveyReminders: { [id: number]: SurveyReminder } };
      surveyId: number;
      changeCount: number;
      notification: Notification;
    }
  | unknown,
  { id: number; surveyId: number }
>('surveyReminder/upsertReminder', async (params, { rejectWithValue }) => {
  try {
    const res = await graphqlRequest<CreateOrUpdateSurveyReminderMutationResult>(CreateOrUpdateSurveyReminderDocument, {
      surveyReminder: params,
    });
    const normalizedData = normalize(res.data?.createOrUpdateSurveyReminder, schemas.surveyReminder);
    const isNew = !params?.id;

    const message = isNew ? 'survey.reminder.create.success' : 'survey.reminder.update.success';

    return {
      ...normalizedData,
      changeCount: isNew ? 1 : 0,
      surveyId: params.surveyId,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t(message),
        status: notificationStatuses.success,
      },
    };
  } catch (e) {
    return rejectWithValue(e);
  }
});

const deleteReminder = createAsyncThunk<
  { id: number; surveyId: number; changeCount: number; notification: Notification } | unknown,
  { id: number; surveyId: number }
>('surveyReminder/deleteReminder', async ({ id, surveyId }, { rejectWithValue }) => {
  try {
    await graphqlRequest<DeleteSurveyReminderMutationResult>(DeleteSurveyReminderDocument, { reminderId: id });

    return {
      id,
      surveyId,
      changeCount: -1,
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('survey.reminder.delete.success'),
        status: notificationStatuses.success,
      },
    };
  } catch (e) {
    return rejectWithValue(e);
  }
});

const openNewReminder = createAsyncThunk<{ surveyReminder: SurveyReminder } | unknown, { surveyId: number }>(
  'surveyReminder/openNewReminder',
  async (surveyId, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest<FindSurveyReminderDetailsBySurveyIdQueryResult>(
        FindSurveyReminderDetailsBySurveyIdDocument,
        { id: surveyId },
      );
      const newSurveyReminder = transformers.newSurveyReminderFrom(res.data?.survey);

      return { surveyReminder: newSurveyReminder };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const openDuplicateReminder = createAsyncThunk<{ surveyReminder: SurveyReminder }, { reminderId: number }>(
  'surveyReminder/openDuplicateReminder',
  async (reminderId, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest<any>(FindSurveyReminderByReminderIdDocument, {
        reminderId,
      });
      const surveyReminder = {
        ...res.data?.findSurveyReminderByReminderId,
        id: null,
        scheduledDateTime: datetime.add(new Date(), { minutes: reminderSettings.EXTRA_MINUTES }).toISOString(),
      };

      return { surveyReminder };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

const openExistingReminder = createAsyncThunk<{ surveyReminder: SurveyReminder }, { reminderId: number }>(
  'surveyReminder/openExistingReminder',
  async (reminderId, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest<any>(FindSurveyReminderByReminderIdDocument, {
        reminderId,
      });
      return { surveyReminder: res.data?.findSurveyReminderByReminderId };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);
const resetReminder = createAction('surveyReminder/resetReminder');

const openConfirmDeleteReminder = createAction('surveyReminder/openConfirmDeleteReminder', (reminderId, surveyId) => ({
  payload: { id: reminderId, surveyId },
}));

const resetDeleteReminder = createAction('surveyReminder/resetDeleteReminder');

export const surveyReminderActions = {
  findSurveyRemindersBySurveyId,
  upsertReminder,
  deleteReminder,
  openNewReminder,
  openDuplicateReminder,
  openExistingReminder,
  resetReminder,
  openConfirmDeleteReminder,
  resetDeleteReminder,
};
